import React from 'react';
import {
  Flex,
  Text,
  Radio,
  Checkbox,
  Box,
  Input,
  Badge,
} from '@chakra-ui/react';
import * as FaIcons from 'react-icons/fa6';
import * as BsIcons from 'react-icons/bs';

import ImageComponent from './ImageComponent';
import DOMPurify from 'dompurify';

const OptionComponent = ({
  option,
  isSelected,
  onSelect,
  view = 'list',
  choice = 'single',
  isDisabled = false,
  variant = 'body-sm-min-reg',
  iconVariant = '',
  ariaLabel = '',
}) => {
  const isCheckbox = choice === 'multiple';

  // State for input validation errors (only used for input views)
  const [inputError, setInputError] = React.useState('');

  // Determine the icon set and icon component
  let IconComponent = option.icon ? FaIcons[option.icon] : null;
  if (option.iconSet === 'bs') {
    IconComponent = option.icon ? BsIcons[option.icon] : null;
  }

  const hoverStyles = {
    bg: isDisabled ? 'white' : option.bgColor || 'var(--vibrant-purple-15)',
    borderColor: isDisabled ? 'gray.300' : 'var(--sutter-sync-purple)',
  };

  const handleRowSelect = () => {
    if (!isDisabled && typeof onSelect === 'function') {
      onSelect();
    }
  };

  if (view === 'list') {
    return (
      <Box
        onClick={handleRowSelect}
        role="button"
        bg={isSelected ? 'var(--vibrant-purple-15, #EEEAFD)' : 'white'}
        color="var(--color-dusky-teal, #1F3433)"
        fontWeight={option.fontWeight ? option.fontWeight : "400"}
        borderColor="var(--sutter-sync-purple, #5A2D82)"
        borderWidth="2px"
        borderRadius="md"
        textAlign="left"
        justifyContent="flex-start"
        _hover={hoverStyles}
        px={4}
        py={4}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        opacity={isDisabled ? 0.6 : 1}
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        aria-label={ariaLabel || `Option: ${option.label}`}
        tabIndex={isDisabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowSelect();
          }
        }}
        _focusVisible={{
          outline: '2px solid var(--sutter-sync-purple, #5A2D82)',
          outlineOffset: '2px',
        }}
        height="auto"
        minHeight="auto"
        whiteSpace="normal"
      >
        <Flex alignItems="center" gap={2} width="100%">
          {isCheckbox ? (
            <Checkbox
              value={option.value}
              isChecked={isSelected}
              isDisabled={isDisabled}
              isReadOnly
              isFocusable={false}
              pointerEvents="none"
              borderColor="var(--sutter-sync-purple, #5A2D82)"
              aria-checked={isSelected}
              aria-label={ariaLabel || `Checkbox for ${option.label}`}
              sx={{
                '&[data-checked]': {
                  bg: 'var(--sutter-sync-purple, #5A2D82)',
                  borderColor: 'var(--sutter-sync-purple, #5A2D82)',
                  color: 'white',
                },
              }}
            />
          ) : (
            <Radio
              value={option.value}
              isChecked={isSelected}
              isDisabled={isDisabled}
              isReadOnly
              isFocusable={false}
              pointerEvents="none"
              borderColor="var(--sutter-sync-purple, #5F1B93)"
              aria-checked={isSelected}
              aria-label={ariaLabel || `Radio button for ${option.label}`}
              sx={{
                '&[data-checked]': {
                  bg: 'var(--sutter-sync-purple, #5F1B93)', // Background for checked state
                  borderColor: 'var(--sutter-sync-purple, #5F1B93)', // Border color for checked state
                  color: 'white', // Text or icon color inside the radio button
                },
                '&:hover': {
                  bg: 'var(--sutter-sync-purple, #5F1B93)', // Hover background
                  color: 'white',
                },
                '&[data-hover]': {
                  bg: 'var(--sutter-sync-purple, #5F1B93)',
                  color: 'white',
                },
                '&[data-focus]': {
                  boxShadow: '0 0 0 2px var(--sutter-sync-purple, #5F1B93)', // Focus ring
                },
                '&[data-checked]:hover': {
                  bg: 'var(--sutter-sync-purple, #5F1B93)', // Hover style for checked item
                  borderColor: 'var(--sutter-sync-purple, #5F1B93)', // Ensure border matches theme
                  color: 'white', // Keep text/icon color consistent
                },
              }}
            />
          )}
          <Box
            fontSize={`var(--${variant}-font-size, 16px)`}
            flex="1"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(option.label) }}
          />
        </Flex>
      </Box>
    );
  }

  if (view === 'grid' || view === 'box') {
    return (
      <Box
        onClick={onSelect}
        bg={isSelected ? 'var(--vibrant-purple-15)' : 'white'}
        borderColor="var(--sutter-sync-purple, #5A2D82)"
        borderWidth="1px"
        borderRadius="8px"
        width={`var(--options-${view}-width, 167px)`}
        height={`var(--options-${view}-height, 108px)`}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        textAlign="center"
        position="relative"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        _hover={hoverStyles}
        opacity={isDisabled ? 0.6 : 1}
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
        p={4}
      >
        {/* Checkmark Icon */}
        {isSelected && (
          <Box
            position="absolute"
            top="8px"
            right="8px"
            bg="transparent"
            borderRadius="50%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            p="4px"
            boxShadow="sm"
          >
            <FaIcons.FaCircleCheck
              color="var(--dusky-teal-color, #1F3433)"
              size="16px"
            />
          </Box>
        )}

        {/* Icon Section */}
        <Box
          bg={`var(--${iconVariant}-bg-color, var(--color-breezy-teal-50))`}
          width={`var(--${iconVariant}-size, 40px)`}
          height={`var(--${iconVariant}-size, 40px)`}
          display="flex"
          justifyContent="center"
          alignItems="center"
          borderRadius={`var(--${iconVariant}-border-radius, 50%)`}
          border={`var(--${iconVariant}-border, none)`}
          borderColor={`var(--${iconVariant}-border-color, none)`}
          overflow="hidden"
          mb={2}
        >
          {IconComponent ? (
            <IconComponent
              style={{
                width: `calc(var(--${iconVariant}-size, 40px) * 0.6)`,
                height: `calc(var(--${iconVariant}-size, 40px) * 0.6)`,
                color: `var(--${iconVariant}-color, var(--duskey-teal-color))`,
              }}
            />
          ) : (
            <Text>No Icon</Text>
          )}
        </Box>

        {/* Label Section */}
        <Box
          fontSize={`var(--${variant || '--body-sm-min-reg'}-font-size, 14px)`}
          fontWeight={`var(--${variant || '--body-sm-min-reg'}-font-weight, 400)`}
          color={`var(--${variant || '--body-sm-min-reg'}-color, #203433)`}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(option.label) }}
       />

        {/* Badge */}
        {option.showBadge && option.badgeText && (
          <Box 
            mt={1}
            width={option.badgeVariant ? `var(--${option.badgeVariant}-width, 127px)` : "127px"}
            height={option.badgeVariant ? `var(--${option.badgeVariant}-height, 26px)` : "26px"}
            padding={option.badgeVariant ? `var(--${option.badgeVariant}-padding, 8px)` : "8px"}
            gap={option.badgeVariant ? `var(--${option.badgeVariant}-gap, 4px)` : "4px"}
            borderRadius={option.badgeVariant ? `var(--${option.badgeVariant}-borderRadius, 18px)` : "18px"}
            border={option.badgeVariant ? `var(--${option.badgeVariant}-border, none)` : "none"}
            bg={option.badgeVariant ? `var(--${option.badgeVariant}-bg, #4E911D)` : "#4E911D"}
            opacity={option.badgeVariant ? `var(--${option.badgeVariant}-opacity, 1)` : "1"}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text
              fontFamily={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-font-family, Sora)` : "Sora"}
              fontWeight={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-font-weight, 700)` : "700"}
              fontSize={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-font-size, 14px)` : "14px"}
              lineHeight={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-line-height, 120%)` : "120%"}
              letterSpacing={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-letter-spacing, 0px)` : "0px"}
              textAlign={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-text-align, center)` : "center"}
              textTransform={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-text-transform, none)` : "none"}
              color={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-color, #FFFFFF)` : "#FFFFFF"}
            >
              {option.badgeText}
            </Text>
          </Box>
        )}
      </Box>
    );
  }
  if (view === 'card') {
    return (
      <Flex
        as="button"
        role="option"
        onClick={onSelect}
        disabled={isDisabled}
        direction="row"
        align={option.description ? 'flex-start' : 'center'}
        justify={option.description ? 'flex-start' : 'center'}
        bg={isSelected ? 'var(--vibrant-purple-15)' : 'white'}
        borderColor={
          isSelected
            ? 'var(--sutter-sync-purple, #5A2D82)'
            : 'var(--duskey-teal-color, #203433)'
        }
        borderWidth="2px"
        borderRadius="8px"
        overflow="hidden"
        width="100%"
        position="relative"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        _hover={{
          bg: isDisabled ? 'white' : 'var(--vibrant-purple-15, #F1E0FF)',
        }}
        opacity={isDisabled ? 0.6 : 1}
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
        p={2}
      >
        {/* Image */}
        {option.image && (
          <ImageComponent
            image={option.image}
            variant={option.imageVariant || 'card-image-default'}
            rotation={option.imageRotation || 0}
            marginTop={option.image.marginTop || 0}
            marginBottom={option.image.marginBottom || 0}
          />
        )}

        {/* Content */}
        <Box flex="1" pl="8px" textAlign="left" display="flex" flexDirection="column" justifyContent="center" mt={option.contentMarginTop} ml={option.contentMarginLeft || 2}>
          {/* Badge */}
          {option.showBadge && option.badgeText && (
            <Box 
              mb={2}
              width={option.badgeVariant ? `var(--${option.badgeVariant}-width, 127px)` : "127px"}
              height={option.badgeVariant ? `var(--${option.badgeVariant}-height, 26px)` : "26px"}
              padding={option.badgeVariant ? `var(--${option.badgeVariant}-padding, 8px)` : "8px"}
              gap={option.badgeVariant ? `var(--${option.badgeVariant}-gap, 4px)` : "4px"}
              borderRadius={option.badgeVariant ? `var(--${option.badgeVariant}-borderRadius, 18px)` : "18px"}
              border={option.badgeVariant ? `var(--${option.badgeVariant}-border, none)` : "none"}
              bg={option.badgeVariant ? `var(--${option.badgeVariant}-bg, #4E911D)` : "#4E911D"}
              opacity={option.badgeVariant ? `var(--${option.badgeVariant}-opacity, 1)` : "1"}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text
                fontFamily={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-font-family, Sora)` : "Sora"}
                fontWeight={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-font-weight, 700)` : "700"}
                fontSize={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-font-size, 14px)` : "14px"}
                lineHeight={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-line-height, 120%)` : "120%"}
                letterSpacing={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-letter-spacing, 0px)` : "0px"}
                textAlign={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-text-align, center)` : "center"}
                textTransform={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-text-transform, none)` : "none"}
                color={option.badgeTextVariant ? `var(--${option.badgeTextVariant}-color, #FFFFFF)` : "#FFFFFF"}
              >
                {option.badgeText}
              </Text>
            </Box>
          )}

          {/* Icon */}
          {IconComponent && (
            <Box
              bg={`var(--${iconVariant}-bg-color, #E6FDFC)`}
              width={`var(--${iconVariant}-size, 40px)`}
              height={`var(--${iconVariant}-size, 40px)`}
              display="flex"
              justifyContent="center"
              alignItems="center"
              borderRadius={`var(--${iconVariant}-border-radius, 50%)`}
              border={`var(--${iconVariant}-border, none)`}
              borderColor={`var(--${iconVariant}-border-color, none)`}
              overflow="hidden"
              mb={option.description ? 2 : 0}
            >
              <IconComponent
                style={{
                  width: `calc(var(--${iconVariant}-size, 40px) * 0.6)`,
                  height: `calc(var(--${iconVariant}-size, 40px) * 0.6)`,
                  color: `var(--${iconVariant}-color, var(--sutter-sync-purple))`,
                }}
              />
            </Box>
          )}

          {/* Label */}
          <Box
            fontSize={`var(--${variant || '--body-sm-min-reg'}-font-size, 14px)`}
            fontWeight={`var(--${variant || '--body-sm-min-reg'}-font-weight, 400)`}
            color={`var(--${variant || '--body-sm-min-reg'}-color, #203433)`}
            mb={option.description ? 0 : 4}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(option.label) }}
          />
          {/* Description */}
          {option.description && (
            <Box
              textAlign={`var(--${option.descriptionVariant || '--body-sm-min-reg'}-text-align, left)`}
              fontSize={`var(--${option.descriptionVariant || '--body-sm-min-reg'}-font-size, 12px)`}
              fontWeight={`var(--${option.descriptionVariant || '--body-sm-min-reg'}-font-weight, 400)`}
              color={`var(--${option.descriptionVariant || '--body-sm-min-reg'}-color, #203433)`}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(isDisabled ? `Complete` : option.description)}}
            />
          )}
        </Box>

        {/* Selection Badge */}
        {isSelected && (
          <Box
            position="absolute"
            top="4px"
            right="4px"
            bg="transparent"
            borderRadius="50%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            boxShadow="sm"
          >
            <FaIcons.FaCircleCheck
              color="var(--dusky-teal-color, #1F3433)"
              size="16px"
            />
          </Box>
        )}
      </Flex>
    );
  }

  if (view === 'flow') {
    const isOutline = iconVariant && iconVariant.includes('outline');
    return (
      <Box
      onClick={onSelect}
      bg={isSelected ? option.bgColor || 'var(--vibrant-purple-15)' : 'white'}
      borderColor={'var(--sutter-sync-purple, #5F1B93)'}
      borderWidth="2px"
      display="inline-flex"
      alignItems="center"
      justifyContent={option.justifyContent || 'flex-start'}
      px={option.paddingX !== undefined ? option.paddingX : 4}
      py={option.paddingY !== undefined ? option.paddingY : 2}
      borderRadius={option.borderRadius ? option.borderRadius : '8px'}
      width={option.fullWidth ? '100%' : (option.width || 'auto')}
      height={option.height || undefined}
      minWidth={option.minWidth || undefined}
      maxHeight={option.height ? undefined : '48px'}
      maxWidth={option.fullWidth ? 'none' : option.maxWidth || '200px'}
      textAlign={option.textAlign || 'left'}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      sx={{
        '&:hover': {
        ...hoverStyles,
        '& > div, & > span': {
          color: option.bgColor ? 'white' : '#5F1B93',
        },
        },
        ...(isSelected && {
        '&:hover': {
          bg: option.bgColor || 'var(--vibrant-purple-15)',
          borderColor: 'var(--sutter-sync-purple, #5F1B93)',
          color: option.bgColor ? 'white' : '#5A2D82',
        },
        }),
      }}
      position="relative"
      opacity={isDisabled ? 0.6 : 1}
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      disabled={isDisabled}
      role="option"
      tabIndex={isDisabled ? -1 : 0}
      >
      {/* Icon or Image Section */}
      {(IconComponent || option.image) && (
        <Box
        as={isOutline ? 'div' : 'circle'}
        size={`var(--${iconVariant}-size, 40px)`}
        border={isOutline ? `var(--${iconVariant}-border, none)` : 'none'}
        borderColor={
          isOutline ? `var(--${iconVariant}-border-color, none)` : 'none'
        }
        width={`var(--${iconVariant}-size, 40px)`}
        height={`var(--${iconVariant}-size, 40px)`}
        display="flex"
        justifyContent="center"
        alignItems="center"
        borderRadius={`var(--${iconVariant}-border-radius, 50%)`}
        overflow="hidden"
        mr={4}
        >
        {IconComponent ? (
          <IconComponent
          style={{
            width: `calc(var(--${iconVariant}-size, 40px) * 0.6)`,
            height: `calc(var(--${iconVariant}-size, 40px) * 0.6)`,
            color: `var(--${iconVariant}-color, var(--sutter-sync-purple))`,
          }}
          />
        ) : (
          <ImageComponent
          image={option.image}
          variant={option.imageVariant}
          rotation={option.imageRotation}
          />
        )}
        </Box>
      )}

      {/* Option Label */}
      <Box
        fontFamily={`var(--${option.variant}-font-family, Sora)`}
        fontSize={`var(--${option.variant || 'body-sm-min-reg'}-font-size, 18px)`}
        fontWeight={`var(--${option.variant || 'body-sm-min-reg'}-font-weight, 400)`}
        color={
        isSelected
          ? option.bgColor
          ? 'white'
          : '#5F1B93'
          : `var(--${option.variant || 'cta-text-lg'}-color, #5F1B93)`
        }
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(option.label) }}
        sx={{
        '&:hover': {
          color:
          option.bgColor && !isDisabled
            ? 'white'
            : `var(--${option.variant || 'cta-text-lg'}-color, #5F1B93)`,
        },
        }}
        flex={option.textAlign ? undefined : '1'}
        width={option.textAlign ? '100%' : undefined}
        textAlign={option.textAlign || 'left'}
      />
      </Box>
    );
  }

  // Input field view
  if (view === 'input' || view === 'input-grid') {
    const handleInputChange = (e) => {
      const value = e.target.value;
      
      // For number inputs, validate the input
      if (option.inputType === 'number') {
        // Allow empty string, numbers, and decimal point
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
          // Always allow the input to be updated
          onSelect(value);
          
          // Clear previous error
          setInputError('');
          
          // If empty, no validation needed
          if (value === '') {
            return;
          }
          
          // Check min/max constraints if they exist
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            const isWithinMin = option.min === undefined || numValue >= option.min;
            const isWithinMax = option.max === undefined || numValue <= option.max;
            
            // Set error message if outside bounds
            if (!isWithinMin) {
              setInputError(`Value must be at least ${option.min}`);
            } else if (!isWithinMax) {
              setInputError(`Value must be at most ${option.max}`);
            }
          }
        } else {
          // Invalid number format
          setInputError('Please enter a valid number');
        }
      } else {
        // For non-number inputs, allow any value
        onSelect(value);
        setInputError('');
      }
    };

    const handleKeyDown = (e) => {
      // For number inputs, only prevent non-numeric characters
      if (option.inputType === 'number') {
        // Allow: backspace, delete, tab, escape, enter, decimal point, and numbers
        if ([8, 9, 27, 13, 46, 110, 190].indexOf(e.keyCode) !== -1 ||
            // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            (e.keyCode === 86 && e.ctrlKey === true) ||
            (e.keyCode === 88 && e.ctrlKey === true) ||
            // Allow home, end, left, right, up, down arrows
            (e.keyCode >= 35 && e.keyCode <= 40)) {
          return;
        }
        
        // Ensure that it is a number and stop the keypress
        if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
          e.preventDefault();
        }
      }
    };

    return (
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        width="100%"
      >
        <Box
          fontFamily={`var(--${option.variant}-font-family, Sora)`}
          fontSize={`var(--${option.variant || 'body-sm-min-reg'}-font-size, 18px)`}
          fontWeight={`var(--${option.variant || 'body-sm-min-reg'}-font-weight, 400)`}
          color={`var(--${option.variant || 'body-sm-min-reg'}-color, #5F1B93)`}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(option.label) }}
        />
        <Input
          type={option.inputType || 'text'}
          value={option.value || ''}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={option.placeholder || `Enter ${option.label.toLowerCase()}`}
          isDisabled={isDisabled}
          isRequired={option.required || false}
          isInvalid={!!inputError}
          min={option.min}
          max={option.max}
          borderColor={inputError ? "red.500" : "var(--sutter-sync-purple, #5A2D82)"}
          borderWidth="2px"
          borderRadius="md"
          _focus={{
            borderColor: inputError ? "red.500" : 'var(--sutter-sync-purple, #5A2D82)',
            boxShadow: inputError ? "0 0 0 1px red.500" : '0 0 0 1px var(--sutter-sync-purple, #5A2D82)',
          }}
          _hover={{
            borderColor: inputError ? "red.500" : 'var(--sutter-sync-purple, #5A2D82)',
          }}
          aria-label={option.ariaLabel || ariaLabel || `Input field for ${option.label}`}
          aria-describedby={option.ariaDescribedby}
        />
        {inputError && (
          <Text
            fontSize="sm"
            color="red.500"
            mt={1}
          >
            {inputError}
          </Text>
        )}
        {option.helperText && !inputError && (
          <Text
            fontSize="sm"
            color="gray.600"
            mt={1}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(option.helperText) }}
          />
        )}
      </Box>
    );
  }

  return <Text>Unsupported view: {view}</Text>;
};

export default OptionComponent;